import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { ThemeName } from '@shared/types';

const STORAGE_KEY = 'archivio.theme';
const THEME_BG: Record<ThemeName, string> = {
  vault: '#0B0B0D',
  aurora: '#0A0A12',
  carbon: '#0C0D0C',
  sereno: '#F6F8FB',
  terra: '#F4EFE7',
  mezzanotte: '#080C16',
};

// Temi chiari: status bar iOS/Android con contenuto scuro (DESIGN_TOKENS §1).
const LIGHT_THEMES: ReadonlySet<ThemeName> = new Set<ThemeName>(['sereno', 'terra']);

export const THEMES: { id: ThemeName; name: string; desc: string }[] = [
  { id: 'vault', name: 'Vault', desc: 'Caldo · oro su nero' },
  { id: 'aurora', name: 'Aurora', desc: 'Glass · glow indaco-teal' },
  { id: 'carbon', name: 'Carbon', desc: 'Tecnico · lime' },
  { id: 'sereno', name: 'Sereno', desc: 'Chiaro · blu professionale' },
  { id: 'terra', name: 'Terra', desc: 'Carta calda · terracotta' },
  { id: 'mezzanotte', name: 'Mezzanotte', desc: 'Notte · ciano su navy' },
];

interface ThemeCtx {
  theme: ThemeName;
  setTheme: (t: ThemeName) => void;
}

const Ctx = createContext<ThemeCtx | null>(null);

function readInitial(): ThemeName {
  try {
    const t = localStorage.getItem(STORAGE_KEY) as ThemeName | null;
    if (t && t in THEME_BG) return t;
  } catch {
    /* ignore */
  }
  return 'vault';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>(readInitial);

  const setTheme = useCallback((t: ThemeName) => {
    setThemeState(t);
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      /* ignore */
    }
  }, []);

  // Applica data-theme + aggiorna <meta theme-color> (README §7/§8).
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    const isLight = LIGHT_THEMES.has(theme);
    root.style.colorScheme = isLight ? 'light' : 'dark';
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', THEME_BG[theme]);
    // Status bar iOS (PWA standalone): chiaro → contenuto scuro.
    const statusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (statusBar) statusBar.setAttribute('content', isLight ? 'default' : 'black-translucent');
  }, [theme]);

  return <Ctx.Provider value={{ theme, setTheme }}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTheme deve essere usato dentro <ThemeProvider>');
  return ctx;
}
